import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, getToken } from "../../../software/api";
import { toast } from "sonner";
import { PlusCircle, Trash2, Save, ArrowLeft, ImageOff, Camera, Loader2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRODUCTS = ["Portable Spa", "Portable Swim Spa", "Urban Pool", "Ice Bath"];
const SHAPES = ["Rectangular", "Round", "Square", "Oval"];
const YESNO = ["No", "Yes"];
const blankSpa = () => ({ model: "", spa_type: "Portable Spa", brand: "", shape: "Rectangular",
  retail_price: "", cost_price: "", seats: "", recliners: "", length: "", width: "", height: "", jets: "",
  moulded_neck_jets: "N/A", waterfall: "N/A", aroma_therapy: "N/A", ozone: "N/A", tuff_base: "No",
  insulated_pipes: "No", stainless_jets: "Standard", cycle_filtration: "Standard", wiring: "Electrician Required",
  spa_bra: "N/A", thermal_cover: "", pumps: "", pump_size: "", speed: "", air_pump: "N/A", amps: "",
  load_shed: "", full_load: "", capacity: "", dry_weight: "", persons: "", delivery: "" });

// Left + middle spec columns — [label, key, type]
const LEFT = [
  ["Shape", "shape", "shape"], ["SpaID", "model", "text"], ["Product", "spa_type", "product"], ["Brand", "brand", "text"],
  ["DON'T PAY", "retail_price", "money"], ["SPECIAL PRICE", "cost_price", "special"],
  ["Seats", "seats", "num"], ["Recliners", "recliners", "num"], ["Length", "length", "num"], ["Width", "width", "num"],
  ["Height", "height", "num"], ["Jets", "jets", "num"], ["Moulded Neck Jets", "moulded_neck_jets", "text"],
  ["Waterfall", "waterfall", "text"], ["Aroma Therapy", "aroma_therapy", "text"], ["Ozone", "ozone", "text"],
  ["Tuff Base", "tuff_base", "yesno"], ["Insulated Pipes", "insulated_pipes", "yesno"],
  ["Stainless Jets", "stainless_jets", "text"], ["Cycle Filtration", "cycle_filtration", "text"],
  ["Wiring", "wiring", "text"], ["Spa Bra", "spa_bra", "text"],
];
const MIDDLE = [
  ["Thermal Cover", "thermal_cover", "text"], ["Pumps", "pumps", "text"], ["Pump Size", "pump_size", "text"],
  ["Speed", "speed", "text"], ["Air Pump", "air_pump", "text"], ["Amps", "amps", "num"],
  ["Load Shed Amps", "load_shed", "text"], ["Full Load Amps", "full_load", "text"],
  ["Dry Weight kg", "dry_weight", "text"], ["Capacity L", "capacity", "text"],
];

const COL_ITEMS = [["Shell", "Spa Shell Colours"], ["Cabinet", "Cabinet Colours"], ["Cover", "Hard Cover Colours"]];

export default function SpaEditor({ onExit }) {
  const [spas, setSpas] = useState([]);
  const [colours, setColours] = useState([]);
  const [sel, setSel] = useState(null);       // product being edited (null = none)
  const [draft, setDraft] = useState(null);    // editable spa dict
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const loadSpas = useCallback(async () => setSpas(await api("GET", "/spas/catalogue")), []);
  const loadColours = useCallback(async () => setColours(await api("GET", "/colours")), []);
  useEffect(() => { loadSpas(); loadColours(); }, [loadSpas, loadColours]);

  const openSpa = (p) => { setSel(p); setDraft({ ...blankSpa(), ...(p?.spa || {}) }); setImage(p?.image_url || ""); };
  const addNew = () => { setSel({ id: null, _new: true }); setDraft(blankSpa()); setImage(""); };
  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  const save = async () => {
    if (!draft?.model?.trim()) { toast.error("Enter a SpaID / model name first."); return; }
    setBusy(true);
    try {
      const payload = { spa: draft, image_url: image };
      const saved = sel?._new || !sel?.id
        ? await api("POST", "/products/spa", payload)
        : await api("PATCH", `/products/${sel.id}/spa`, payload);
      toast.success(`${saved.name} saved`);
      await loadSpas();
      setSel(saved); setDraft({ ...blankSpa(), ...(saved.spa || {}) }); setImage(saved.image_url || "");
    } catch (e) { toast.error(e.message || "Save failed"); } finally { setBusy(false); }
  };

  const del = async () => {
    if (!sel?.id) { setSel(null); setDraft(null); return; }
    if (!window.confirm(`Delete "${sel.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await api("DELETE", `/products/${sel.id}`); toast.success("Deleted"); await loadSpas(); setSel(null); setDraft(null); }
    catch (e) { toast.error(e.message || "Delete failed"); } finally { setBusy(false); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choose an image file."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await fetch(`${API}/uploads`, { method: "POST", headers: { Authorization: `Bearer ${getToken()}` }, body: fd });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Upload failed");
      const url = `${process.env.REACT_APP_BACKEND_URL}${d.url}`;
      setImage(url);
      if (sel?.id) await api("PATCH", `/products/${sel.id}/image`, { image_url: url });
      toast.success("Image set");
    } catch (err) { toast.error(err.message || "Upload failed"); } finally { setBusy(false); }
  };

  const num = (v) => { const n = Number(String(v).replace(/[^0-9.]/g, "")); return isNaN(n) ? 0 : n; };
  const money = (v) => "$" + num(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div data-testid="spa-editor-panel" className="text-[13px]">
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="spa-edit-file" />
      {/* Title bar */}
      <div className="flex flex-wrap items-center gap-3 bg-[#1a4f8a] px-3 py-2 rounded-t-md">
        <button onClick={addNew} data-testid="spa-add-btn" className="rounded bg-white px-3 py-1.5 text-sm font-black text-[#1a4f8a] hover:bg-slate-100 inline-flex items-center gap-1.5"><PlusCircle className="h-4 w-4" /> Add Spa</button>
        <label className="text-white text-sm font-bold ml-1">Find Model:</label>
        <select data-testid="spa-find-model" value={sel?.id || ""} onChange={(e) => { const p = spas.find((x) => x.id === e.target.value); if (p) openSpa(p); }} className="min-w-[280px] rounded-sm border border-[#7ba7d9] px-2 py-1 text-[13px]">
          <option value="">— select a spa —</option>
          {spas.map((p) => <option key={p.id} value={p.id}>{p.spa?.model || p.name} — {p.spa?.spa_type || p.category}</option>)}
        </select>
        {sel && <button onClick={del} data-testid="spa-delete-btn" className="ml-auto rounded bg-red-600 px-3 py-1.5 text-sm font-bold text-white hover:bg-red-700 inline-flex items-center gap-1.5"><Trash2 className="h-4 w-4" /> Delete</button>}
        {onExit && <button onClick={onExit} data-testid="spa-close-form" className={`${sel ? "" : "ml-auto"} rounded bg-white/90 px-3 py-1.5 text-sm font-bold text-[#1a4f8a] hover:bg-white inline-flex items-center gap-1.5`}><ArrowLeft className="h-4 w-4" /> Close Form</button>}
      </div>

      {!draft ? (
        <div className="border border-[#9db8d6] border-t-0 rounded-b-md bg-[#eaf1fb] p-10 text-center text-slate-500">
          Select a spa from <b>Find Model</b> to edit it, or click <b>Add Spa</b> to create a new one.
        </div>
      ) : (
        <div className="border border-[#9db8d6] border-t-0 rounded-b-md bg-[#eaf1fb] p-3">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_1fr_1.3fr] gap-4">
            {/* LEFT */}
            <div className="space-y-1">
              {LEFT.map(([label, key, type]) => <Field key={key} label={label} type={type} value={draft[key]} onChange={set(key)} />)}
            </div>
            {/* MIDDLE */}
            <div className="space-y-1">
              {MIDDLE.map(([label, key, type]) => <Field key={key} label={label} type={type} value={draft[key]} onChange={set(key)} />)}
              {/* Total persons (auto = seats + recliners) */}
              <div className="grid grid-cols-[130px_1fr] items-center gap-1 mt-1">
                <span className="text-right pr-1 font-bold text-slate-800">Total Persons:</span>
                <div className="bg-[#fff29a] border border-[#c9b24a] px-2 py-1 font-black text-center" data-testid="spa-total-persons">{num(draft.seats) + num(draft.recliners)}</div>
              </div>
              <div className="grid grid-cols-[130px_1fr] items-center gap-1">
                <span className="text-right pr-1 font-bold text-slate-800">Delivery $:</span>
                <div className="flex items-center gap-1 bg-[#6b4a2b] px-1 py-0.5 rounded-sm">
                  <span className="text-white text-xs pl-1">$</span>
                  <input value={draft.delivery} onChange={set("delivery")} data-testid="spa-f-delivery" className="w-full bg-white px-2 py-0.5 text-center font-bold" />
                </div>
              </div>
              {/* Image */}
              <div className="pt-2">
                <p className="text-xs font-bold text-slate-600 mb-1">Spa Image</p>
                <button onClick={() => fileRef.current?.click()} data-testid="spa-image-upload" className="w-full aspect-[4/3] bg-white border border-[#9db8d6] overflow-hidden flex items-center justify-center hover:border-[#1a4f8a] group relative">
                  {image ? <img src={image} alt="spa" className="h-full w-full object-contain" /> : <ImageOff className="h-8 w-8 text-slate-300" />}
                  <span className="absolute bottom-2 right-2 h-8 w-8 rounded-full bg-white/90 shadow flex items-center justify-center">{busy ? <Loader2 className="h-4 w-4 animate-spin text-[#1a4f8a]" /> : <Camera className="h-4 w-4 text-[#1a4f8a]" />}</span>
                </button>
              </div>
            </div>
            {/* RIGHT — colour lists */}
            <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-1 gap-3 items-start min-w-0">
              {COL_ITEMS.map(([item, title]) => (
                <ColourColumn key={item} item={item} title={title}
                  colours={colours.filter((c) => (c.item || "").toLowerCase() === item.toLowerCase() && (!c.models || (sel?.id && c.models.includes(sel.id))))}
                  library={colours.filter((c) => (c.item || "").toLowerCase() === item.toLowerCase())}
                  spaId={sel?.id} allSpaIds={spas.map((x) => x.id)} onChanged={loadColours} />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3">
            <button onClick={save} disabled={busy} data-testid="spa-save-btn" className="rounded bg-emerald-600 px-5 py-2 text-sm font-black text-white hover:bg-emerald-700 inline-flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Spa</button>
            <span className="text-sm text-slate-600">{sel?._new || !sel?.id ? "New spa" : `Editing: ${sel.name}`} · Don't Pay {money(draft.retail_price)} · Special {money(draft.cost_price)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type }) {
  const special = type === "special";
  const base = "w-full border border-[#9db8d6] px-2 py-1 text-[13px]";
  return (
    <div className="grid grid-cols-[130px_1fr] items-center gap-1">
      <span className={`text-right pr-1 font-bold ${special ? "bg-[#b6e04a] px-1 py-0.5 text-slate-900" : "text-slate-800"}`}>{label}:</span>
      {type === "shape" ? (
        <select value={value || ""} onChange={onChange} className={base}>{SHAPES.map((s) => <option key={s}>{s}</option>)}</select>
      ) : type === "product" ? (
        <select value={value || ""} onChange={onChange} data-testid="spa-f-product" className={base}>{PRODUCTS.map((s) => <option key={s}>{s}</option>)}</select>
      ) : type === "yesno" ? (
        <select value={value || "No"} onChange={onChange} className={base}>{YESNO.map((s) => <option key={s}>{s}</option>)}</select>
      ) : (
        <input value={value ?? ""} onChange={onChange} inputMode={type === "num" || type === "money" ? "decimal" : "text"}
          className={`${base} ${special ? "bg-[#d9f3a8] font-bold" : ""}`} />
      )}
    </div>
  );
}

// Colours are a shared library; each colour links to the models it's offered on (models null = every model).
function ColourColumn({ item, title, colours, library, spaId, allSpaIds, onChanged }) {
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const name = adding.trim();
    if (!name) return;
    if (!spaId) { toast.error("Save the spa first, then add its colours."); return; }
    setBusy(true);
    try {
      const ex = library.find((c) => c.name.toLowerCase() === name.toLowerCase());
      if (ex) { if (ex.models) await api("PATCH", `/colours/${ex.id}`, { name: ex.name, models: [...new Set([...ex.models, spaId])] }); }
      else await api("POST", "/colours", { name, item, models: [spaId] });
      setAdding(""); await onChanged(); toast.success("Colour added to this model");
    } catch (e) { toast.error(e.message || "Failed"); } finally { setBusy(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from this model? (It stays in the colour library.)`)) return;
    const models = (c.models || allSpaIds).filter((id) => id !== spaId);
    try { await api("PATCH", `/colours/${c.id}`, { name: c.name, models }); await onChanged(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="bg-white border border-[#9db8d6] rounded-sm flex flex-col isolate" data-testid={`spa-colours-${item.toLowerCase()}`}>
      <p className="text-center font-black text-[#1a4f8a] border-b border-[#9db8d6] py-1 text-[13px]">{title}</p>
      <div className="p-1.5 space-y-1 flex-1 min-h-0 max-h-[300px] overflow-y-auto">
        {colours.length === 0 && <p className="text-xs text-slate-400 text-center py-2">None yet</p>}
        {colours.map((c) => (
          <div key={c.id} className="flex items-center gap-1">
            <span className="flex-1 border border-[#9db8d6] px-2 py-0.5 text-[12px] truncate" title={c.name}>{c.name}</span>
            <button onClick={() => remove(c)} data-testid={`spa-colour-del-${c.id}`} className="text-red-600 hover:text-red-700 shrink-0"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-1 p-1.5 border-t border-[#9db8d6]">
        <input value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Sandstone + $500" list={`spa-col-lib-${item}`} data-testid={`spa-colour-add-${item.toLowerCase()}`} className="flex-1 border border-[#9db8d6] px-2 py-1 text-[12px]" />
        <datalist id={`spa-col-lib-${item}`}>{library.map((c) => <option key={c.id} value={c.name} />)}</datalist>
        <button onClick={add} disabled={busy} className="rounded bg-[#1a4f8a] p-1.5 text-white hover:bg-[#123a63] disabled:opacity-60" data-testid={`spa-colour-addbtn-${item.toLowerCase()}`}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlusCircle className="h-3.5 w-3.5" />}</button>
      </div>
    </div>
  );
}
